/**
 * Trading Lots Selection and Strike Stepper Helpers.
 *
 * Populates lot options with dynamic broker lot sizing (e.g. 65 Nifty, 15 Bank Nifty, 20 Sensex)
 * and clamps strike navigation within dropdown bounds.
 */

export interface LotOption {
  value: number;
  label: string;
}

/**
 * Generates options list for lot selection dropdown.
 */
export function generateLotsOptions(lotSize: number, maxLots: number = 300): LotOption[] {
  const options: LotOption[] = [];
  const max = Math.max(1, maxLots || 300);

  for (let n = 1; n <= max; n++) {
    const qtyText = lotSize && lotSize > 0 ? String(n * lotSize) : "—";
    options.push({
      value: n,
      label: `${n} lot (Qty ${qtyText})`,
    });
  }

  return options;
}

export interface PaperQuantityOption {
  value: number;
  text: string;
}

/**
 * Generates options list for paper trading quantity dropdown (e.g. "1 Lot (65)", "2 Lots (130)").
 */
export function generatePaperQuantityOptions(lotSize: number, maxLots: number = 300): PaperQuantityOption[] {
  const options: PaperQuantityOption[] = [];
  const size = lotSize > 0 ? lotSize : 1;
  const max = Math.max(1, maxLots || 300);

  for (let lots = 1; lots <= max; lots++) {
    const qty = lots * size;
    options.push({
      value: qty,
      text: `${lots} Lot${lots > 1 ? "s" : ""} (${qty})`,
    });
  }

  return options;
}

/**
 * Computes safe next selected index for stepping strikes (+/-).
 */
export function computeSteppedIndex(
  currentIndex: number,
  delta: number,
  totalOptions: number,
): number {
  if (totalOptions <= 0) return 0;
  const next = currentIndex + delta;
  return Math.max(0, Math.min(next, totalOptions - 1));
}

